/**
 * 量化淘金 - 常量、类型与接口数据映射
 * courseNo: 5f0a51df3bf44351aa50b1df798c9836
 */

export const QUANT_GOLD_COURSE_NO = '5f0a51df3bf44351aa50b1df798c9836'

/** 查看往期可选历史交易日数量（与 indexPool.json historyDays 一致） */
export const QUANT_GOLD_HISTORY_DAYS = 30

const INDEX_POOL_ASSET_BASE = 'https://pcstatic.sylapp.cn/helpmanual/assets/indexPool/5f0a51df3bf44351aa50b1df798c9836'

/** indexPool.json 中量化淘金卡片配置（接口未返回时兜底） */
export const QUANT_GOLD_PRODUCT_CARD = {
  courseNo: QUANT_GOLD_COURSE_NO,
  poolName: '淘金指',
  poolNameImg: `${INDEX_POOL_ASSET_BASE}/poolNameImg.png`,
  poolNameImgDark: `${INDEX_POOL_ASSET_BASE}/poolNameImgDark.png`,
  remark: '*数据来源于交易所和第三方数据库，筹码大师存在一定局限性，所涉及任何内容及操作仅供您参考和学习使用，不构成投资建议，不构成对任何金融产品的购买、抛售或持有，不作为您做出任何投资的依据，不承诺您获得投资收益，也不与您约定分享收益或分担损失，本产品不作任何担保。您应自主做出投资决策， 自行承担投资风险和损失。投资有风险，入市须谨慎。',
  tags: ['AI监控', '进场信号', '离场信号'],
  content: '指标简介指标简介指标简介指标简介指标简介指标简介指标简介指标简介指标简介',
  disclaimer: '该产品由九方智投投顾王宁(登记编号A0740622090011)主导开发。',
  poolIcon: `${INDEX_POOL_ASSET_BASE}/poolIcon.png`,
  historyDays: String(QUANT_GOLD_HISTORY_DAYS)
}

export type QuantGoldMainTab = 'newChose' | 'dataTrack' | 'hotCase' | 'beforeChose'
export type QuantGoldSubTab = 'entrySignal' | 'exitSignal' | 'shortSignal' | 'coverShortSignal'
export interface QuantGoldSelectedPlate {
  symbol: string
  name: string
  plateType?: string
}

export interface QuantGoldFilterParams {
  upSpaceStars: number[]
  plateCodes?: QuantGoldSelectedPlate[]
  onlyOptional?: boolean
  optionalSymbols?: string[]
}

export interface QuantGoldPoolItem {
  symbol: string
  name: string
  market: string
  lastPx: number
  pxChangRate: number
  price: number
  upSpaceStar?: number
  highPct: number
  plateName: string
  plateHeatRank: number
  cumRate: number
  sellTime?: string | null
  entryTime?: string | null
  coverShortTime?: string | null
  shortTime?: string | null
  selectTime?: number
  exitDate?: string | null
  amount?: number
  isOptional?: boolean
  /** 最高价：接口 highPx / 订阅 HighestPrice（进场实时最高涨幅） */
  highPx?: number
  /** 最低价：列表订阅 LowestPrice（字段 low=14） */
  lowPx?: number
  /** 接口原始 highPct，供 WS 重算 */
  rawHighPct?: number | null
  /** 接口原始 periodPct，供 WS 重算 */
  rawPeriodPct?: number | null
  ext?: {
    plateCode?: string
    plateName?: string
    refTime?: number
    star?: number
    plateHot?: number
    [key: string]: any
  }
}

export type QuantGoldStatPeriod = 2 | 3 | 4

/** 热门案例统计周期：2近3月 3近1年 4至今 */
export const QUANT_GOLD_STAT_PERIODS: { value: QuantGoldStatPeriod; label: string }[] = [
  { value: 2, label: '近3月' },
  { value: 3, label: '近1年' },
  { value: 4, label: '至今' }
]

/** 数据追踪统计周期：2近3月 3近6月 4至今 */
export const QUANT_GOLD_TRACK_PERIODS: { value: QuantGoldStatPeriod; label: string }[] = [
  { value: 2, label: '近3月' },
  { value: 3, label: '近6月' },
  { value: 4, label: '至今' }
]

/** 热门案例默认统计周期 */
export const QUANT_GOLD_HOT_CASE_PERIOD: QuantGoldStatPeriod = 2

/** 量化淘金 ladder/list 策略码 */
export const QUANT_GOLD_STRATEGY_CODE: Record<QuantGoldSubTab, string> = {
  entrySignal: 'quantgold_buy',
  exitSignal: 'quantgold_sell',
  shortSignal: 'quantgold_short',
  coverShortSignal: 'quantgold_cover'
}

function formatQuantGoldDate(val: number | string): string {
  const n = Number(val)
  const ms = n < 1e12 ? n * 1000 : n
  const d = new Date(ms)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** 产品卡片兜底（与 indexPool.json 字段一致） */
export function getQuantGoldProductFallback() {
  return [{ ...QUANT_GOLD_PRODUCT_CARD }]
}

/** 接口日历 timeList → YYYY-MM-DD 数组（降序，最新在前，与四维猎手 historyDates 一致） */
export function parseQuantGoldTimeList(timeList: any): string[] {
  if (!timeList) return []
  const raw = Array.isArray(timeList) ? timeList : []
  const dates = raw.map((item: any) => {
    if (typeof item === 'string') return item.slice(0, 10)
    if (typeof item === 'number') return formatQuantGoldDate(item)
    if (item?.tradingDay != null) return formatQuantGoldDate(item.tradingDay)
    return ''
  }).filter(Boolean)
  return normalizeQuantGoldHistoryDates(dates)
}

/** 历史可选日期降序排列 */
export function normalizeQuantGoldHistoryDates(dates: string[]): string[] {
  return dates.filter(Boolean).slice().sort((a, b) => b.localeCompare(a))
}

/** 取最新可选历史日期 */
export function getLatestQuantGoldHistoryDate(dates: string[]): string {
  return normalizeQuantGoldHistoryDates(dates)[0] || ''
}

/** YYYY-MM-DD → 毫秒时间戳（与四维猎手 handleDateChange 一致） */
export function toQuantGoldTradingDayTimestamp(dateStr: string | { format?: (fmt: string) => string }): number {
  const normalized = typeof dateStr === 'string'
    ? dateStr.slice(0, 10)
    : (dateStr?.format ? dateStr.format('YYYY-MM-DD') : '')
  const parts = normalized.split('-').map(Number)
  if (parts.length !== 3 || parts.some(n => !Number.isFinite(n))) {
    const n = Number(dateStr)
    return Number.isFinite(n) ? n : 0
  }
  return new Date(parts[0], parts[1] - 1, parts[2]).getTime()
}

function formatQuantGoldRefTime(val: number | string | null | undefined): string | null {
  if (val === null || val === undefined || val === '' || val === 0 || val === '0') return null
  const n = Number(val)
  if (!Number.isFinite(n) || n <= 0) return String(val).slice(0, 10) || null
  return formatQuantGoldDate(n)
}

/** 标准化市场代码，便于 WebSocket 匹配 */
export function normalizeQuantGoldMarket(market: string, symbol?: string): string {
  let m = (market || '').toLowerCase()
  if (m === 'sse' || m === 'sha' || m === 'shetf' || m === '1') m = 'sh'
  else if (m === 'szse' || m === 'sza' || m === 'szetf' || m === '0') m = 'sz'
  else if (m === 'bse' || m === '2') m = 'bj'
  if (!m && symbol) {
    const code = String(symbol)
    if (code.startsWith('6') || code.startsWith('5')) m = 'sh'
    else if (code.startsWith('0') || code.startsWith('3') || code.startsWith('1')) m = 'sz'
    else if (code.startsWith('4') || code.startsWith('8')) m = 'bj'
  }
  return m || 'sz'
}

function resolveQuantGoldRefTime(item: any, ext: Record<string, any>): number | string | null | undefined {
  return item.refTime ?? ext.refTime ?? null
}

/** ext.refTime == 0（或空）时走实时计算；非 0 直接用接口字段 */
export function isQuantGoldRefTimeZero(refTime: any): boolean {
  if (refTime === null || refTime === undefined || refTime === '') return true
  const n = Number(refTime)
  return !Number.isFinite(n) || n === 0
}

function toFiniteNumber(val: any): number | null {
  if (val === null || val === undefined || val === '') return null
  const n = Number(val)
  return Number.isFinite(n) ? n : null
}

/**
 * 最高涨幅 / 最高跌幅（按小数存储，展示 *100）
 * - 进场：refTime!=0 → highPct；==0 → max((highPx-入选价)/入选价, highPct)
 * - 离场 / 平空：highPct
 * - 看空：refTime!=0 → highPct（>0 视为 0）；==0 → min((lowPx-入选价)/入选价, highPct)
 *   比较前 highPct>0 按 0；lowPx 取列表订阅字段 low=14
 */
export function calcQuantGoldHighPct(options: {
  subTab: QuantGoldSubTab
  refTime: any
  selectedPrice: number | null
  highPx?: number | null
  lowPx?: number | null
  highPct: number | null
}): number | null {
  const { subTab, refTime, selectedPrice, highPx, lowPx } = options
  let highPct = options.highPct

  if (subTab === 'exitSignal' || subTab === 'coverShortSignal') {
    return highPct
  }

  // 看空：接口 highPct > 0 时按 0.0% 处理
  if (subTab === 'shortSignal' && highPct != null && highPct > 0) {
    highPct = 0
  }

  if (!isQuantGoldRefTimeZero(refTime)) {
    return highPct
  }

  if (selectedPrice == null || selectedPrice === 0) {
    return highPct
  }

  if (subTab === 'entrySignal') {
    if (highPx == null) return highPct
    const livePct = (highPx - selectedPrice) / selectedPrice
    if (highPct == null) return livePct
    return Math.max(livePct, highPct)
  }

  if (subTab === 'shortSignal') {
    if (lowPx == null) return highPct
    const livePct = (lowPx - selectedPrice) / selectedPrice
    // highPct 为空时按 0 参与 min（最高跌幅不应显示为正涨幅）
    const comparePct = highPct == null ? 0 : highPct
    return Math.min(livePct, comparePct)
  }

  return highPct
}

/**
 * 累计涨幅（按小数存储，展示 *100）
 * - 进场 / 看空：refTime!=0 → periodPct；==0 → (订阅最新价-入选价)/入选价
 * - 离场 / 平空：periodPct
 */
export function calcQuantGoldCumRate(options: {
  subTab: QuantGoldSubTab
  refTime: any
  selectedPrice: number | null
  lastPx?: number | null
  periodPct: number | null
}): number | null {
  const { subTab, refTime, selectedPrice, lastPx, periodPct } = options

  if (subTab === 'exitSignal' || subTab === 'coverShortSignal') {
    return periodPct
  }

  if (!isQuantGoldRefTimeZero(refTime)) {
    return periodPct
  }

  if (selectedPrice == null || selectedPrice === 0 || lastPx == null) {
    return periodPct
  }

  return (lastPx - selectedPrice) / selectedPrice
}

/** WebSocket 推送后，重算进场/看空在 refTime==0 时的最高涨(跌)幅与累计涨幅 */
export function refreshQuantGoldLiveMetrics(item: any, subTab: QuantGoldSubTab): void {
  if (subTab !== 'entrySignal' && subTab !== 'shortSignal') return
  const refTime = item?.ext?.refTime ?? item?.refTime
  if (!isQuantGoldRefTimeZero(refTime)) return

  const selectedPrice = toFiniteNumber(item.price ?? item.selectedPrice)
  const lastPx = toFiniteNumber(item.lastPx)
  const highPx = toFiniteNumber(item.highPx)
  const lowPx = toFiniteNumber(item.lowPx)
  const rawHighPct = toFiniteNumber(item.rawHighPct)
  const periodPct = toFiniteNumber(item.rawPeriodPct)

  item.highPct = calcQuantGoldHighPct({
    subTab,
    refTime,
    selectedPrice,
    highPx,
    lowPx,
    highPct: rawHighPct
  })
  item.cumRate = calcQuantGoldCumRate({
    subTab,
    refTime,
    selectedPrice,
    lastPx,
    periodPct
  })
}

/** 量化淘金股池接口项 → 列表行 */
export function mapQuantGoldPoolItem(item: any, subTab: QuantGoldSubTab): QuantGoldPoolItem {
  const symbol = String(item.symbol || '').padStart(6, '0')
  let ext = item.ext
  if (typeof ext === 'string') {
    try {
      ext = ext ? JSON.parse(ext) : {}
    } catch {
      ext = {}
    }
  }
  ext = ext || {}
  const plateName = ext.plateName ?? item.plateName ?? item.plate?.name ?? ''
  const refTime = resolveQuantGoldRefTime(item, ext)
  const selectedPrice = toFiniteNumber(item.selectedPrice ?? item.price)
  const highPx = toFiniteNumber(item.highPx)
  const lowPx = toFiniteNumber(item.lowPx)
  const lastPx = toFiniteNumber(item.lastPx)
  const rawHighPct = toFiniteNumber(item.highPct)
  const rawPeriodPct = toFiniteNumber(item.periodPct ?? item.cumRate)

  const highPct = calcQuantGoldHighPct({
    subTab,
    refTime,
    selectedPrice,
    highPx,
    lowPx,
    highPct: rawHighPct
  })
  const cumRate = calcQuantGoldCumRate({
    subTab,
    refTime,
    selectedPrice,
    lastPx,
    periodPct: rawPeriodPct
  })

  return {
    symbol,
    name: item.name,
    market: normalizeQuantGoldMarket(item.market, symbol),
    lastPx: item.lastPx,
    pxChangRate: item.pxChangeRate ?? item.pxChangRate ?? 0,
    price: item.selectedPrice ?? item.price,
    upSpaceStar: ext.star ?? item.star ?? item.upSpaceStar,
    highPct: highPct as number,
    plateName,
    plateHeatRank: ext.plateHot ?? item.plateHot ?? 0,
    cumRate: cumRate as number,
    // 进场信号 Tab：离场信号时间 ← refTime；离场信号 Tab 无此列
    sellTime: subTab === 'entrySignal'
      ? formatQuantGoldRefTime(refTime)
      : undefined,
    // 离场信号 Tab：进场信号时间 ← refTime；进场信号 Tab 无此列
    entryTime: subTab === 'exitSignal'
      ? formatQuantGoldRefTime(refTime)
      : undefined,
    // 看空信号 Tab：平空信号时间 ← refTime
    coverShortTime: subTab === 'shortSignal'
      ? formatQuantGoldRefTime(refTime)
      : undefined,
    // 平空信号 Tab：看空信号时间 ← refTime
    shortTime: subTab === 'coverShortSignal'
      ? formatQuantGoldRefTime(refTime)
      : undefined,
    amount: item.amount,
    highPx: highPx ?? undefined,
    lowPx: lowPx ?? undefined,
    rawHighPct,
    rawPeriodPct,
    ext: { ...ext, refTime: refTime ?? ext.refTime }
  }
}

/** 构建量化淘金股池请求参数 */
export function buildQuantGoldPoolQuery(options: {
  subTab: QuantGoldSubTab
  mainTab: QuantGoldMainTab
  pageNo: number
  pageSize: number
  onlyOptional: number
  userName?: string
  sortField: string
  sortType: string
  filters: QuantGoldFilterParams
  historyDays: number
  historyDate?: string
  sortFieldMap: Record<string, string>
  includeTradingDay?: boolean
}) {
  // 进场/看空的最高涨(跌)幅、累计涨幅由前端行情重算，接口常为 null；
  // 传给服务端排序会导致刷新后行序与展示值不一致，改为本地排序
  const liveMetricClientSortFields = ['highPct', 'cumRate']
  const isLiveMetricClientSort =
    (options.mainTab === 'newChose' || options.mainTab === 'beforeChose') &&
    (options.subTab === 'entrySignal' || options.subTab === 'shortSignal') &&
    liveMetricClientSortFields.includes(options.sortField)

  const apiSortField = (!isLiveMetricClientSort && options.sortField)
    ? (options.sortFieldMap[options.sortField] || options.sortField)
    : ''
  const queryData: Record<string, any> = {
    strategyCode: QUANT_GOLD_STRATEGY_CODE[options.subTab],
    pageNo: options.pageNo,
    pageSize: options.pageSize,
    onlyOptional: options.onlyOptional,
    days: options.historyDays
  }
  if (options.onlyOptional === 1) {
    queryData.userName = options.userName || ''
  }
  if (apiSortField) {
    queryData.sortField = apiSortField
    queryData.sortType = options.sortType || 'desc'
  }
  // 上涨空间、题材板块筛选仅进场信号支持
  if (options.subTab === 'entrySignal') {
    if (options.filters.upSpaceStars?.length) {
      queryData.star = options.filters.upSpaceStars.join(',')
    }
    if (options.filters.plateCodes?.length) {
      queryData.plateCodes = options.filters.plateCodes
        .map(p => p.symbol)
        .filter(Boolean)
        .join(',')
    }
  }
  if (
    options.includeTradingDay !== false &&
    options.mainTab === 'beforeChose' &&
    options.historyDate
  ) {
    queryData.tradingDay = toQuantGoldTradingDayTimestamp(options.historyDate)
  }
  return queryData
}

/** 热门案例接口项 → 列表行（最新/涨幅/成交额由 WebSocket 推送） */
export function mapQuantGoldHotCaseItem(item: any) {
  const symbol = String(item.symbol || '').padStart(6, '0')
  return {
    symbol,
    name: item.name,
    market: normalizeQuantGoldMarket(item.market, symbol),
    price: item.selectPrice,
    selectTime: item.selectTime,
    highPct: item.rangeRate,
    exitDate: item.sellTime,
    lastPx: null as number | null,
    pxChangRate: null as number | null,
    amount: null as number | null
  }
}
